import { useState } from 'react';

import type { Achievement } from '../engine/achievements.ts';
import type { Move } from '../engine/modes.ts';

// ***************************************************************************************
// The solution: the words the grid was BUILT from, in the order that empties it.
//
// Every constructed board starts as a solution. The generator carves disjoint paths out
// of the grid, settling between each exactly as play does, and only then gives each path
// a real word of its own length (see engine/board.ts). So this list is not a solver's
// answer to the board - it is the thing the board was made out of.
//
// TWO THINGS TO KNOW ABOUT IT.
//
// The ORDER is the solution, not the words. Play one and everything above it falls
// somewhere else, so the same five words in a different order usually do not clear the
// board. That is why this is a numbered route rather than a word list.
//
// It is ONE solution, not the only one, and a player is not held to it. Deviate and the
// steps below stop describing the board in front of you - which the panel says, rather
// than leaving a list that is now wrong sitting there looking authoritative.
//
// HIDDEN BY DEFAULT. In Hunt it answers some of the day's achievements outright ("find
// the word that means...") - so it is opened deliberately, and the choice is remembered.
// The server decides whether it is sent at all (REVEAL_SOLUTION); this only decides
// whether what was sent is on screen.
// ***************************************************************************************

const HIDE_KEY = 'wordsoup.solution.hidden';

/** How many of the played words follow the stored route from the start. */
export function routeProgress(
  moves: readonly { word: string }[],
  solution: readonly string[],
): number {
  let followed = 0;

  while (followed < moves.length && moves[followed]!.word === solution[followed]) followed++;

  return followed;
}

function readHidden(): boolean {
  try {
    // Default HIDDEN: an unset key is a player who has never asked to see it.
    return localStorage.getItem(HIDE_KEY) !== 'false';
  } catch {
    return true;
  }
}

function writeHidden(hidden: boolean): void {
  try {
    localStorage.setItem(HIDE_KEY, hidden ? 'true' : 'false');
  } catch {
    /* a blocked store just means the choice does not survive a reload */
  }
}

export function SolutionPanel({
  solution,
  moves,
  achievements = [],
}: {
  /** The generator's words, in play order. */
  solution: readonly string[];
  moves: readonly Move[];
  /** The day's challenges. Only those carrying an `example` are listed. */
  achievements?: readonly Achievement[];
}): React.ReactElement | null {
  const [hidden, setHidden] = useState(readHidden);

  if (solution.length === 0) return null;

  // Only the ones the server sent an answer for. On a site that does not reveal
  // solutions the field is stripped, and this list is then empty rather than wrong.
  const answered = achievements.filter((achievement) => achievement.example);

  const followed = routeProgress(moves, solution);
  const strayed = followed < moves.length;
  const tiles = solution.reduce((total, word) => total + word.length, 0);

  return (
    <section className="solution" aria-label="Solution">
      <div className="solution__head">
        <h2 className="solution__title">Solution</h2>
        <button
          className="solution__toggle"
          type="button"
          aria-expanded={!hidden}
          onClick={() => {
            const next = !hidden;

            setHidden(next);
            writeHidden(next);
          }}
        >
          {hidden ? 'Show' : 'Hide'}
        </button>
      </div>

      {hidden ? (
        <p className="solution__note">
          {solution.length} words, {tiles} tiles — the route this board was built from.
        </p>
      ) : (
        <>
          <ol className="solution__list">
            {solution.map((word, index) => {
              const played = index < followed;
              const next = !strayed && index === followed;

              return (
                <li
                  key={`${word}-${index}`}
                  className={
                    'solution__step' +
                    (played ? ' solution__step--done' : '') +
                    (next ? ' solution__step--next' : '')
                  }
                >
                  <span className="solution__number">{index + 1}</span>
                  <span className="solution__word">{word}</span>
                  <span className="solution__length">{word.length}</span>
                  <span className="solution__mark" aria-hidden="true">
                    {played ? '✓' : next ? '▸' : ''}
                  </span>
                </li>
              );
            })}
          </ol>

          {/* ***********************************************************************************
              The achievements' answers.
              
              A separate question from the route, and the one a stuck player actually has:
              is the answer even on this board? The route is seven words out of the several
              hundred a 5x5 offers, so a challenge being absent from it says nothing - and
              without this there is no way to tell "hard" from "impossible".
              *********************************************************************************** */}
          {answered.length > 0 ? (
            <>
              <h3 className="solution__subtitle">One word that takes each</h3>

              <ul className="solution__list solution__list--answers">
                {answered.map((achievement) => (
                  <li key={achievement.id} className="solution__step">
                    <span className="solution__clue">{achievement.label}</span>
                    <span className="solution__word">{achievement.example}</span>
                  </li>
                ))}
              </ul>

              <p className="solution__note">
                Checked against this board before the puzzle was published — every
                challenge has at least one word here that takes it. These are the words
                that proved it, not the only ones that work.
              </p>
            </>
          ) : null}

          <p className="solution__note">
            {strayed
              ? 'You have played a word the route did not plan for, so the steps below it describe a board that no longer exists. Undo to get back to it, or find your own way — there is more than one way to empty a grid.'
              : 'These are the words the grid was built from, in the order that empties it. Play one and the rest fall somewhere else, so the order is doing most of the work.'}
          </p>
        </>
      )}
    </section>
  );
}
